<script setup lang="ts">
import { useTemplateRef, watch } from 'vue';
import { useEventListener } from '@vueuse/core';

import { ruText as t } from '@/constants/ru-text';

const isOpen = defineModel<boolean>();

const emit = defineEmits<{
  close: [];
  cancel: [];
}>();

const filterDialogEl = useTemplateRef<HTMLDialogElement>('find-replay-el');

const open = () => {
  isOpen.value = true;
  filterDialogEl.value?.showModal();
};

const close = () => {
  isOpen.value = false;
  filterDialogEl.value?.close();
};

watch(isOpen, () => (isOpen.value ? open() : close()));

const onCloseHandler = () => close();
useEventListener(filterDialogEl, 'close', onCloseHandler);
useEventListener(filterDialogEl, 'cancel', () => emit('cancel'));
</script>

<template>
  <dialog
    ref="find-replay-el"
    class="dialog"
  >
    <div class="wrap">
      <div class="head">
        <button
          class="g-icon material-symbols-rounded close-btn round"
          :title="t.get('component-dialog-close-title')"
          @click="close()"
        >
          close
        </button>
        <h1 v-if="$slots.title">
          <slot name="title"></slot>
        </h1>
      </div>
      <slot @close="close()" />
    </div>
  </dialog>
</template>

<style scoped lang="postcss">
.dialog {
  --function: ease-out;

  padding: var(--space-m-var_03);

  width: 100%;
  max-width: 100vw;
  height: 100%;
  max-height: 100vw;
  overflow: hidden;

  border: none;
  background: transparent;

  transition:
    opacity var(--duration) var(--function),
    overlay var(--duration) var(--function) allow-discrete,
    display var(--duration) var(--function) allow-discrete;
  opacity: 0;

  @media (--fhd-desktop) {
    padding: 36px 88px;
  }

  .wrap {
    display: grid;
    grid-template-rows: auto 1fr;

    margin: 0 auto;
    width: 100%;

    max-width: var(--container-max-width);

    .close-btn {
      position: absolute;
      top: 0;
      right: 0;
      padding: var(--space-s-var_02);
      background: transparent;
      color: var(--color-white-50);
      font-size: 28px;
      transform: translate(-52px, 30px);
      cursor: pointer;

      &:hover {
        background-color: var(--color-white-4);
        color: var(--color-white-80);
      }
    }

    h1 {
      margin-bottom: var(--space-m-var_03);
      padding-bottom: var(--space-m-var_03);
      border-bottom: 1px solid var(--color-white-12);
    }
  }
}

dialog[open] {
  opacity: 1;
}

@starting-style {
  dialog[open] {
    opacity: 0;
  }
}

dialog::backdrop {
  background-color: transparent;
  transition:
    background-color var(--duration) var(--function),
    backdrop-filter var(--duration) var(--function),
    overlay var(--duration) var(--function) allow-discrete,
    display var(--duration) var(--function) allow-discrete;
  backdrop-filter: blur(0px);
}

dialog[open]::backdrop {
  background-color: var(--color-deep-blue-85);
  backdrop-filter: blur(80px);
}

@starting-style {
  dialog[open]::backdrop {
    background-color: transparent;
    backdrop-filter: blur(0px);
  }
}
</style>
